/* import __COLOCATED_TEMPLATE__ from './phone-number-modal.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import type ApplicationInstance from '@ember/application/instance';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type PhoneNumber from 'embercom/models/calling-phone-number';
import { PhoneNumberStatus } from 'embercom/models/calling-phone-number';
import type Calling from 'embercom/models/settings/calling';
import {
  PHONE_NUMBER_COUNTRY_DETAILS,
  countryNeedsBundleForNumberType,
} from 'embercom/models/settings/calling';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type IntlService from 'embercom/services/intl';
import type Admin from 'embercom/models/admin';
import { FIN_VOICE_VOICE_LANGUAGE_OPTIONS } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import { DEFAULT_TTS_LANGUAGE } from 'embercom/objects/calling/tts-language-options';

type OutboundCsatSelection = 'default' | 'enabled' | 'disabled';

interface Args {
  phoneNumber: PhoneNumber;
  closeModal: () => void;
  callSettings?: Calling;
}

interface Signature {
  Args: Args;
}

export default class PhoneNumberModal extends Component<Signature> {
  readonly missingBundle = PhoneNumberStatus.MissingBundle;

  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare intercomCallService: IntercomCallService;
  @service declare notificationsService: any;

  @tracked showDeleteModal = false;
  @tracked showClearDefaultConfirmModal = false;
  @tracked selectedRegulatoryBundle?: string;
  @tracked setAsDefault: boolean;

  get phoneNumberFlag() {
    return PHONE_NUMBER_COUNTRY_DETAILS.get(this.args.phoneNumber.countryIsoCode)?.flag;
  }

  // Gates the per-line CSAT controls behind the rollout flag so the backend (which is
  // gated by the same phone-per-line-outbound-csat flag) and the UI stay in lockstep.
  get canUsePerLineOutboundCsat() {
    return this.appService.app.canUseFeature('phone-per-line-outbound-csat');
  }

  get languageOptions() {
    let options = FIN_VOICE_VOICE_LANGUAGE_OPTIONS;

    // Filter out Tamil if the feature flag is not enabled
    if (!this.appService.app.canUseFinVoiceTamilSupport) {
      options = options.filter((option) => option.value !== 'ta-IN');
    }

    // Filter out Multilingual if the feature flag is not enabled
    if (!this.appService.app.canUseFinVoiceMultilingual) {
      options = options.filter((option) => option.value !== 'multilingual');
    }

    return options.map((option) => ({
      value: option.value,
      text: this.intl.t(option.text),
    }));
  }

  get selectedLocale() {
    return this.args.phoneNumber.locale || undefined;
  }

  get noTeamsForApp() {
    return this.appService.app.assignableTeams.length === 0;
  }

  get listOfAssignableTeams() {
    let teams = this.appService.app.assignableTeams;
    return teams.map((team: Admin) => this.avatarItem(team));
  }

  avatarItem(admin: Admin) {
    return {
      text: admin.get('name'),
      value: admin.get('id'),
      component: 'common/avatar-and-name-dropdown-item',
      componentAttrs: {
        model: admin,
        modelDisplayName: admin.get('name'),
        avatarSize: 'xs',
      },
    };
  }

  get listOfAssignableAdminsAndTeams() {
    let allAdmins = this.appService.app.admins;
    let teams = allAdmins.filter((admin: Admin) => admin.get('isTeam') === true);
    let admins = allAdmins.filter((admin: Admin) => admin.get('isTeam') === false);

    return [
      {
        heading: this.intl.t('calling.settings.phone-calls.phone-number.routing.teams'),
        items: teams.map((team: Admin) => this.avatarItem(team)),
      },
      {
        heading: this.intl.t('calling.settings.phone-calls.phone-number.routing.teammates'),
        items: admins.map((admin: Admin) => this.avatarItem(admin)),
      },
    ];
  }

  get shouldShowVoicemailSettings() {
    return (
      this.args.phoneNumber.routingMethod === 'workspace' ||
      this.args.phoneNumber.routingMethod === 'team'
    );
  }

  get needsRegulatoryBundle() {
    return countryNeedsBundleForNumberType(
      this.args.phoneNumber.countryIsoCode,
      this.args.phoneNumber.phoneType,
    );
  }

  get missingBundleBannerText() {
    if (this.countryCode === 'AU') {
      return this.intl.t('calling.settings.phone-calls.phone-number.banner.missing-bundles-au');
    }
    return this.intl.t('calling.settings.phone-calls.phone-number.banner.missing-bundles');
  }

  get countryCode() {
    return this.args.phoneNumber.countryIsoCode;
  }

  get phoneType() {
    return this.args.phoneNumber.phoneType;
  }

  get regulatoryBundleSid() {
    return this.args.phoneNumber.regulatoryBundleSid;
  }

  get phoneNumberStatus() {
    return this.args.phoneNumber.status;
  }

  get isUpdatePhoneNumberDisabled() {
    return this.isMaxQueueSizeInvalid;
  }

  get isMaxQueueSizeInvalid() {
    if (!this.args.phoneNumber.voicemailMaxQueueSizeTriggerEnabled) {
      return false;
    }
    return (
      isEmpty(this.args.phoneNumber.voicemailMaxQueueSize) ||
      this.args.phoneNumber.voicemailMaxQueueSize < 0
    );
  }

  constructor(owner: ApplicationInstance, args: Args) {
    super(owner, args);
    this.setAsDefault = this.isCurrentDefault;

    if (this.noTeamsForApp) {
      this.args.phoneNumber.assignTeamId = '0';
    } else {
      this.args.phoneNumber.assignTeamId =
        this.args.phoneNumber.assignTeamId || this.listOfAssignableTeams[0]?.value;
    }
  }

  get isCurrentDefault(): boolean {
    return (
      this.args.callSettings?.defaultOutboundPhoneNumberId === Number(this.args.phoneNumber.id)
    );
  }

  get previousDefaultPhoneNumber(): PhoneNumber | undefined {
    let id = this.args.callSettings?.defaultOutboundPhoneNumberId;
    if (id == null) {
      return undefined;
    }
    let allPhoneNumbers = this.store.peekAll('calling-phone-number');
    return allPhoneNumbers.find((number: PhoneNumber) => Number(number.id) === id) as
      | PhoneNumber
      | undefined;
  }

  private phoneNumberDisplayName(phoneNumber: PhoneNumber): string {
    return phoneNumber.name
      ? `${phoneNumber.name} (${phoneNumber.formattedPhoneNumber})`
      : (phoneNumber.formattedPhoneNumber ?? phoneNumber.phoneNumber);
  }

  openPortingTicket() {
    window.Intercom('showTicket', this.args.phoneNumber.portingTicketId);
  }

  @action
  toggleDefaultOutbound() {
    this.setAsDefault = !this.setAsDefault;
  }

  @action
  toggleOutboundCalls() {
    if (this.args.phoneNumber.outboundEnabled && this.isCurrentDefault) {
      this.showClearDefaultConfirmModal = true;
      return;
    }
    this.args.phoneNumber.outboundEnabled = !this.args.phoneNumber.outboundEnabled;
  }

  @action
  confirmClearDefaultAndDisableOutbound() {
    this.setAsDefault = false;
    this.args.phoneNumber.outboundEnabled = false;
    this.showClearDefaultConfirmModal = false;
  }

  @action
  toggleInboundCalls() {
    this.args.phoneNumber.inboundEnabled = !this.args.phoneNumber.inboundEnabled;
  }

  @action selectRegulatoryBundle(regulatoryBundleSid: string) {
    this.args.phoneNumber.regulatoryBundleSid = regulatoryBundleSid;
  }

  @action
  selectLocale(locale: string) {
    this.args.phoneNumber.locale = locale;
  }

  @action
  savePhoneNumberSettings() {
    let defaultChanged = this.setAsDefault !== this.isCurrentDefault;
    if (defaultChanged && this.args.callSettings) {
      if (this.setAsDefault) {
        let previousDefault = this.previousDefaultPhoneNumber;
        this.args.callSettings.defaultOutboundPhoneNumberId = Number(this.args.phoneNumber.id);
        this.args.callSettings.save();

        let newName = this.phoneNumberDisplayName(this.args.phoneNumber);
        if (previousDefault) {
          let previousName = this.phoneNumberDisplayName(previousDefault);
          this.notificationsService.notifyConfirmation(
            this.intl.t('calling.settings.default-outbound-number.toast-changed', {
              previousNumber: previousName,
              newNumber: newName,
            }),
          );
        } else {
          this.notificationsService.notifyConfirmation(
            this.intl.t('calling.settings.default-outbound-number.toast-set', {
              newNumber: newName,
            }),
          );
        }
      } else {
        this.args.callSettings.defaultOutboundPhoneNumberId = null;
        this.args.callSettings.save();
        this.notificationsService.notifyConfirmation(
          this.intl.t('calling.settings.default-outbound-number.toast-cleared'),
        );
      }
      this.intercomCallService.isWorkspacePhoneNumbersLoaded = false;
    }

    this.args.phoneNumber.save();
    this.args.closeModal();
  }

  @action
  deletePhoneNumber() {
    if (this.isCurrentDefault && this.args.callSettings) {
      this.args.callSettings.defaultOutboundPhoneNumberId = null;
      this.args.callSettings.save();
      this.intercomCallService.isWorkspacePhoneNumbersLoaded = false;
    }
    this.args.phoneNumber.destroyRecord();
    this.args.closeModal();
  }

  @action
  setAssignAdminId(adminId: string) {
    this.args.phoneNumber.assignAdminId = adminId.toString();
    if (this.args.phoneNumber.assignAdmin.get('isTeam') === true) {
      this.args.phoneNumber.assignTeamId = adminId;
      this.args.phoneNumber.routingMethod = 'team';
    } else {
      this.args.phoneNumber.routingMethod = 'teammate';
    }
  }

  get teamOrTeammate() {
    if (this.args.phoneNumber.isActiveFreeUsageNumber) {
      if (this.args.phoneNumber.assignAdmin?.get('isTeam') === true) {
        return 'team';
      } else {
        return 'teammate';
      }
    } else {
      return 'team';
    }
  }

  get outboundCsatSelection(): OutboundCsatSelection {
    let value = this.args.phoneNumber.outboundCsatEnabled;
    if (value == null) {
      return 'default';
    }
    return value ? 'enabled' : 'disabled';
  }

  // RadioGroup binds two-way, so the user's choice arrives through this setter (same
  // pattern as inbox2/search/cvda-list-filter). Clearing message + language for
  // "default"/"disabled" is intentional: the backend resolves each field independently
  // (`outbound_csat_message.presence || workspace.outbound_csat_message`), so a leftover
  // per-line value would silently override the workspace default via inheritance.
  set outboundCsatSelection(value: OutboundCsatSelection) {
    let { phoneNumber } = this.args;
    switch (value) {
      case 'default':
        phoneNumber.outboundCsatEnabled = null;
        this.clearPerLineCsatOverrides();
        break;
      case 'enabled':
        phoneNumber.outboundCsatEnabled = true;
        break;
      case 'disabled':
        phoneNumber.outboundCsatEnabled = false;
        this.clearPerLineCsatOverrides();
        break;
    }
  }

  get outboundCsatTtsLanguageForDisplay(): string {
    return (
      this.args.phoneNumber.outboundCsatTtsLanguage ??
      this.args.callSettings?.outboundCsatTtsLanguage ??
      DEFAULT_TTS_LANGUAGE
    );
  }

  private clearPerLineCsatOverrides() {
    this.args.phoneNumber.outboundCsatMessage = null;
    this.args.phoneNumber.outboundCsatTtsLanguage = null;
  }

  @action
  updatePerLineCsatMessage(event: Event) {
    this.args.phoneNumber.outboundCsatMessage = (event.currentTarget as HTMLTextAreaElement).value;
  }

  @action
  selectPerLineCsatLanguage(language: string) {
    this.args.phoneNumber.outboundCsatTtsLanguage = language;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Calling::Settings::PhoneNumberModal': typeof PhoneNumberModal;
    'calling/settings/phone-number-modal': typeof PhoneNumberModal;
  }
}
